/** 格式化：数字、金额、百分比、时间、标识缩写。
 *
 * 端口自原型 `docs/design/操作台原型` 的 `ui.js`——原型已经把口径定死了
 * （缺失一律显示 `—` 而不是 0、正负号与色一起给、时间一律 UTC），这里只做等价迁移，
 * 不顺手动口径。
 */

/** 缺失值统一显示 `—`。**不许把缺失显示成 0**：0 是一个业务结论。 */
export const DASH = "—";

export function num(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function money(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const sign = value < 0 ? "-" : "";
  return `${sign}$${num(Math.abs(value), 2)}${currency ? ` ${currency}` : ""}`;
}

/** 带符号的金额：正数补 `+`，由调用点决定颜色（`tone`）。 */
export function signedMoney(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return (value > 0 ? "+" : "") + money(value, currency);
}

export function pct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return `${(value * 100).toFixed(digits)}%`;
}

export function signedPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return (value > 0 ? "+" : "") + pct(value, digits);
}

/** 语义色：正 / 负 / 零。与 `styles.css` 的 `.pos` / `.neg` / `.dim` 对应。 */
export function tone(value: number | null | undefined): "pos" | "neg" | "dim" {
  if (value === null || value === undefined || Number.isNaN(value) || value === 0) return "dim";
  return value > 0 ? "pos" : "neg";
}

/** 纳秒 → `2023-11-14 22:13:20Z`（UTC，与内核时间基准一致）。 */
export function ts(ns: number | null | undefined): string {
  if (ns === null || ns === undefined || Number.isNaN(ns)) return DASH;
  return new Date(Math.floor(ns / 1e6)).toISOString().replace("T", " ").replace(".000Z", "Z");
}

/** 只取 `HH:MM:SS`——密集表格里的时间列。 */
export function clock(ns: number | null | undefined): string {
  if (ns === null || ns === undefined || Number.isNaN(ns)) return DASH;
  return ts(ns).slice(11, 19);
}

/** 长标识缩写：`abcdefghij…wxyz`。短到不用缩就原样返回。 */
export function short(id: string | null | undefined, keep = 10): string {
  if (!id) return DASH;
  return id.length <= keep + 4 ? id : `${id.slice(0, keep)}…${id.slice(-4)}`;
}
